/**
 * <TodaysWorkPanel> · ADR-FO-1 AR-6
 * ────────────────────────────────────────────────────────────────
 * Receptionist's operational checklist. Groups of rows the receptionist must
 * work through today: arrivals, departures, pending payments, approvals,
 * VIP arrivals, engineering holds, housekeeping delays.
 *
 * Pure UI — data flows in via `sections` prop. Each section is collapsible.
 */
import React, { useState } from "react";
import {
  ChevronDown, ChevronRight, CalendarCheck, CalendarX, CreditCard,
  ShieldCheck, Crown, Wrench, Sparkles, LogIn, LogOut, Loader2,
} from "lucide-react";
import { StatusChip, OperationalCard, OperationalButton } from "./index";

const _DEFAULT_SECTIONS = [
  { key: "arrivals",           label: "Arrivals",             icon: CalendarCheck, tone: "active" },
  { key: "departures",         label: "Departures",           icon: CalendarX,     tone: "active" },
  { key: "pending_checkins",   label: "Pending Check-ins",    icon: LogIn,         tone: "pending" },
  { key: "pending_checkouts",  label: "Pending Check-outs",   icon: LogOut,        tone: "pending" },
  { key: "pending_payments",   label: "Pending Payments",     icon: CreditCard,    tone: "attention" },
  { key: "approvals",          label: "Approvals Waiting",    icon: ShieldCheck,   tone: "attention" },
  { key: "vip_arrivals",       label: "VIP Arrivals",         icon: Crown,         tone: "active" },
  { key: "engineering_holds",  label: "Engineering Holds",    icon: Wrench,        tone: "attention" },
  { key: "hk_delays",          label: "Housekeeping Delays",  icon: Sparkles,      tone: "pending" },
];

export function TodaysWorkPanel({
  sections = _DEFAULT_SECTIONS,   // section templates
  data = {},                       // { [key]: { count, items:[{id,label,sublabel,status?,action?}] } }
  loading = false,
  defaultCollapsed = false,
  onRowClick,                      // (sectionKey, item) => ...
  renderTrailing,                  // optional (sectionKey, item) => node — inline control at the row's end
  testid = "todays-work-panel",
}) {
  return (
    <div data-testid={testid} className="space-y-2">
      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-[13px] p-4"
              data-testid={`${testid}-loading`}>
          <Loader2 className="w-4 h-4 animate-spin"/> Loading today's work…
        </div>
      ) : (
        sections.map(s => (
          <Section
            key={s.key}
            section={s}
            data={data[s.key]}
            defaultOpen={!defaultCollapsed}
            onRowClick={onRowClick}
            renderTrailing={renderTrailing}
            testid={`${testid}-${s.key}`}
          />
        ))
      )}
    </div>
  );
}

function Section({ section, data, defaultOpen, onRowClick, renderTrailing, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  const Icon = section.icon;
  const count = data?.count ?? 0;
  const items = data?.items ?? [];
  const isEmpty = count === 0 && items.length === 0;

  return (
    <OperationalCard padded={false} testid={testid}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid={`${testid}-toggle`}
        className="w-full min-h-[56px] flex items-center gap-3 p-4 text-left
                    hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-4
                    focus-visible:ring-sky-300 rounded-t-xl"
        aria-expanded={open}
      >
        {open ? <ChevronDown className="w-5 h-5 text-slate-500"/>
              : <ChevronRight className="w-5 h-5 text-slate-500"/>}
        <Icon className="w-5 h-5 text-slate-700"/>
        <span className="text-[15px] font-semibold flex-1">{section.label}</span>
        <StatusChip
          status={count > 0 ? section.tone : "ready"}
          label={isEmpty ? "0" : String(count)}
          size="md"
          testid={`${testid}-count`}/>
      </button>

      {open && (
        <div className="border-t border-slate-200" data-testid={`${testid}-body`}>
          {isEmpty ? (
            <div className="p-6 text-center text-[13px] text-slate-400"
                    data-testid={`${testid}-empty`}>
              Nothing pending in this section.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((it, i) => (
                <li key={it.id || i}
                    className="p-3 flex items-center gap-3 hover:bg-slate-50 min-h-[48px]"
                    data-testid={`${testid}-row-${i}`}>
                  {it.status && <StatusChip status={it.status} size="sm"/>}
                  <div className="flex-1 min-w-0"
                        onClick={() => onRowClick?.(section.key, it)}
                        role={onRowClick ? "button" : undefined}
                        tabIndex={onRowClick ? 0 : undefined}
                        onKeyDown={(e) => {
                          if (onRowClick && (e.key === "Enter" || e.key === " ")) onRowClick(section.key, it);
                        }}>
                    <div className="text-[14px] font-medium truncate">{it.label}</div>
                    {it.sublabel && (
                      <div className="text-[12px] text-slate-500 truncate">{it.sublabel}</div>
                    )}
                  </div>
                  {renderTrailing?.(section.key, it)}
                  {it.action && (
                    <OperationalButton {...it.action}
                       size="secondary" tone={it.action.tone || "secondary"}
                       testid={`${testid}-row-${i}-action`}/>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </OperationalCard>
  );
}
